import React, { useRef, useEffect } from "react";
import { X } from "lucide-react";
import { useI18n } from "./i18n";

const dialogStack: HTMLDivElement[] = [];
const background = new Map<Element, { inert: string | null; hidden: string | null }>();
function restoreBackground() {
  for (const [element, original] of background) {
    for (const [name, value] of [['inert', original.inert], ['aria-hidden', original.hidden]] as const) {
      if (value === null) element.removeAttribute(name); else element.setAttribute(name, value);
    }
  }
  background.clear();
}
function containBackground() {
  restoreBackground();
  const top = dialogStack.at(-1);
  for (const dialog of dialogStack) {
    if (dialog !== top && !dialog.contains(top ?? null)) dialog.setAttribute('aria-hidden', 'true');
    else dialog.removeAttribute('aria-hidden');
    if (dialog === top) dialog.setAttribute('aria-modal', 'true');
    else dialog.removeAttribute('aria-modal');
  }
  // Inert siblings along the ancestor path, never an ancestor of a nested dialog.
  // Native recipe dialogs use the browser's top layer and manage their own modality.
  let branch: Element | undefined = top?.parentElement ?? undefined;
  while (branch?.parentElement) {
    for (const sibling of branch.parentElement.children) {
      if (sibling === branch || sibling.matches('dialog, script, style, link')) continue;
      background.set(sibling, { inert: sibling.getAttribute('inert'), hidden: sibling.getAttribute('aria-hidden') });
      sibling.setAttribute('inert', '');
      sibling.setAttribute('aria-hidden', 'true');
    }
    branch = branch.parentElement;
    if (branch === document.body) break;
  }
}
function tabbableChildren(element: HTMLElement) {
  return Array.from(element.querySelectorAll<HTMLElement>(
    'button, input, select, textarea, a[href], summary, [tabindex], [contenteditable="true"], audio[controls], video[controls]',
  )).filter(candidate => {
    if (candidate.tabIndex < 0 || candidate.matches(':disabled') || candidate.closest('[hidden], [inert]')) return false;
    for (let ancestor: HTMLElement | null = candidate; ancestor; ancestor = ancestor.parentElement) {
      const style = getComputedStyle(ancestor);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') return false;
      if (ancestor instanceof HTMLDetailsElement && !ancestor.open && !ancestor.querySelector('summary')?.contains(candidate)) return false;
      if (ancestor === element) break;
    }
    return true;
  }).sort((a, b) => (a.tabIndex || Infinity) - (b.tabIndex || Infinity));
}

export function Dialog({ title, onClose, children }: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const { t } = useI18n();
  const ref = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const old = document.activeElement as HTMLElement | null;
    const element = ref.current!;
    dialogStack.push(element);
    // Release the parent's inert scope before focusing a newly mounted sibling dialog.
    restoreBackground();
    element.focus();
    containBackground();
    let lastFocus: HTMLElement = element;
    const active = () => dialogStack.at(-1) === element && !document.querySelector('dialog[open]');
    const focus = (event: FocusEvent) => {
      if (!active()) return;
      const target = event.target;
      if (target instanceof HTMLElement && element.contains(target)) lastFocus = target;
      else (tabbableChildren(element).includes(lastFocus) ? lastFocus : element).focus();
    };
    const key = (event: KeyboardEvent) => {
      if (!active()) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        closeRef.current();
      }
      if (event.key !== 'Tab') return;
      const all = tabbableChildren(element);
      const first = all[0], last = all.at(-1);
      if (!first || !all.includes(document.activeElement as HTMLElement)) {
        event.preventDefault();
        (event.shiftKey ? last ?? element : first ?? element).focus();
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last!.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', key);
    document.addEventListener('focusin', focus);
    // Catalog/auth updates can add background controls while the modal is open.
    const observer = new MutationObserver(() => { if (dialogStack.at(-1) === element) containBackground(); });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => {
      observer.disconnect();
      document.removeEventListener('keydown', key);
      document.removeEventListener('focusin', focus);
      const wasTop = dialogStack.at(-1) === element;
      const index = dialogStack.indexOf(element);
      if (index >= 0) dialogStack.splice(index, 1);
      containBackground();
      const top = dialogStack.at(-1);
      if (wasTop) {
        if (old?.isConnected && (!top || top.contains(old)) && !old.closest('[inert]')) old.focus();
        if (top && !top.contains(document.activeElement)) top.focus();
      }
    };
  }, []);
  return <div className="modal-backdrop" onMouseDown={event => {
    if (event.target === event.currentTarget && dialogStack.at(-1) === ref.current) onClose();
  }}>
    <div ref={ref} tabIndex={-1} className="modal" role="dialog" aria-modal="true" aria-label={title}>
      <div className="modal-heading">
        <h2>{title}</h2>
        <button onClick={onClose} aria-label={t('common.closeDialog')}><X size={20} /></button>
      </div>
      {children}
    </div>
  </div>;
}
